import { z } from 'zod';
import { CONTENT_PERIOD_KINDS, CONTENT_PLATFORMS, EXCEL_MAX_ROW, TOV_MAX_LENGTH, } from '../types/content-calendar.js';
/**
 * YYYY-MM-DD. The regex guards format; the refine catches non-existent
 * calendar dates the regex would accept (e.g. "2024-02-30", "2024-13-01").
 */
const IsoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
    .refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(s);
}, { message: 'Invalid calendar date' });
// v2.0.0 PR #C — systemPrompt / model / temperature / maxTokens dropped;
// owned by the Content Calendar AgentProfile (slug='content_calendar').
export const PatchContentCalendarConfigRequestSchema = z
    .object({
    topKChunks: z.number().int().min(0).max(20).optional(),
    // Empty string is meaningful: it clears the folder. `.optional()` alone
    // couldn't express "explicitly set to none" versus "left untouched".
    driveSheetFolderUrl: z.string().max(2_000).optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const CreateContentCampaignRequestSchema = z
    .object({
    topic: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(10_000),
    knowledgeNotes: z.string().max(20_000).optional(),
    tov: z.string().trim().max(TOV_MAX_LENGTH).optional(),
    platforms: z.array(z.enum(CONTENT_PLATFORMS)).min(1).max(CONTENT_PLATFORMS.length),
    includeHashtags: z.boolean(),
    periodKind: z.enum(CONTENT_PERIOD_KINDS),
    startDate: IsoDate,
    endDate: IsoDate.optional(),
    scheduled: z.boolean(),
})
    .refine((d) => (d.periodKind === 'custom' ? !!d.endDate && d.endDate >= d.startDate : true), { message: 'endDate required (and ≥ startDate) when periodKind=custom', path: ['endDate'] });
/**
 * One row of an uploaded sheet, submitted on its own so each request stays well
 * inside nginx's 300s proxy timeout. `periodKind` and `endDate` are deliberately
 * absent — the server forces `single`, because one row is always one post.
 */
export const BulkGenerateRowRequestSchema = z.object({
    // The SHEET's row number, for error messages only — it is never an index
    // into anything. Bounding it by the 10-row cap rejected the last row of
    // every full sheet, since ten data rows run from row 2 to row 11.
    rowNumber: z.number().int().min(1).max(EXCEL_MAX_ROW),
    topic: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(10_000),
    knowledgeNotes: z.string().max(20_000).optional(),
    tov: z.string().trim().max(TOV_MAX_LENGTH).optional(),
    platforms: z.array(z.enum(CONTENT_PLATFORMS)).min(1).max(CONTENT_PLATFORMS.length),
    includeHashtags: z.boolean(),
    startDate: IsoDate,
    scheduled: z.boolean(),
});
export const UpdateContentPostRequestSchema = z
    .object({
    variants: z
        .array(z.object({
        id: z.string().uuid(),
        body: z.string().max(20_000).optional(),
        prompts: z.array(z.string().max(4_000)).max(20).optional(),
        tovs: z.array(z.string().max(TOV_MAX_LENGTH)).max(20).optional(),
    }))
        .max(6)
        .optional(),
    selectedCaptionVariantId: z.string().uuid().nullable().optional(),
    selectedVisualVariantId: z.string().uuid().nullable().optional(),
    scheduledFor: z.string().datetime({ offset: true }).nullable().optional(),
    status: z.enum(['draft', 'scheduled', 'published']).optional(),
    postDate: IsoDate.optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const ContentCalendarIdParamSchema = z.object({ id: z.string().uuid() });
export const ContentCalendarPostIdParamSchema = z.object({ postId: z.string().uuid() });
export const ContentCalendarPostsQuerySchema = z.object({
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
export const GrantContentCalendarBodySchema = z.object({ userId: z.string().uuid() });
/**
 * Share — package selected posts into a combined PDF and send via internal
 * mail to the given recipients. `postIds` is what the user clicked; the
 * server expands per-day groupings into PDF sections.
 */
export const ShareContentRequestSchema = z.object({
    postIds: z.array(z.string().uuid()).min(1).max(200),
    recipientUserIds: z.array(z.string().uuid()).min(1).max(50),
});
//# sourceMappingURL=content-calendar.schema.js.map